import React, { useEffect, useRef, useState } from "react";
import { Music, VolumeX, ChevronUp, ChevronDown } from "lucide-react";

// 80s Weather Channel style synth loop — Am -> F -> C -> G pads with a gentle
// arpeggio and soft bass, generated live with WebAudio (no audio files needed).
const CHORDS = [
  [57, 60, 64], // Am
  [53, 57, 60], // F
  [48, 52, 55], // C
  [55, 59, 62], // G
];
const BEAT = 0.36; // seconds per step
const STEPS_PER_CHORD = 8;

const midiToFreq = (m) => 440 * Math.pow(2, (m - 69) / 12);

class SynthLoop {
  constructor() {
    this.ctx = null;
    this.timer = null;
    this.step = 0;
    this.nextTime = 0;
    this.volume = 0.5;
  }

  start() {
    if (!this.ctx) {
      this.ctx = new (window.AudioContext || window.webkitAudioContext)();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.volume * 0.6;
      this.master.connect(this.ctx.destination);
    }
    if (this.ctx.state === "suspended") this.ctx.resume();
    this.step = 0;
    this.nextTime = this.ctx.currentTime + 0.1;
    this.timer = setInterval(() => this.schedule(), 120);
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    if (this.ctx) this.ctx.suspend();
  }

  setVolume(v) {
    this.volume = v;
    if (this.master) this.master.gain.setTargetAtTime(v * 0.6, this.ctx.currentTime, 0.05);
  }

  schedule() {
    while (this.nextTime < this.ctx.currentTime + 0.45) {
      this.playStep(this.step, this.nextTime);
      this.step += 1;
      this.nextTime += BEAT;
    }
  }

  playStep(step, t) {
    const chordIdx = Math.floor(step / STEPS_PER_CHORD) % CHORDS.length;
    const chord = CHORDS[chordIdx];
    const beatInChord = step % STEPS_PER_CHORD;

    // Warm pad at each chord change
    if (beatInChord === 0) {
      const dur = BEAT * STEPS_PER_CHORD;
      chord.forEach((m) => {
        const osc = this.ctx.createOscillator();
        osc.type = "triangle";
        osc.frequency.value = midiToFreq(m);
        const lp = this.ctx.createBiquadFilter();
        lp.type = "lowpass";
        lp.frequency.value = 1400;
        const g = this.ctx.createGain();
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(0.055, t + 0.5);
        g.gain.setValueAtTime(0.055, t + dur - 0.6);
        g.gain.linearRampToValueAtTime(0, t + dur);
        osc.connect(lp).connect(g).connect(this.master);
        osc.start(t);
        osc.stop(t + dur + 0.05);
      });
      // Soft bass root
      const bass = this.ctx.createOscillator();
      bass.type = "sine";
      bass.frequency.value = midiToFreq(chord[0] - 12);
      const bg = this.ctx.createGain();
      bg.gain.setValueAtTime(0, t);
      bg.gain.linearRampToValueAtTime(0.09, t + 0.15);
      bg.gain.setTargetAtTime(0, t + dur - 0.8, 0.3);
      bass.connect(bg).connect(this.master);
      bass.start(t);
      bass.stop(t + dur + 0.05);
    }

    // Gentle arpeggio, one octave up
    const arpNote = chord[[0, 1, 2, 1][beatInChord % 4]] + 12;
    const osc = this.ctx.createOscillator();
    osc.type = "square";
    osc.frequency.value = midiToFreq(arpNote);
    const lp = this.ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 2200;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.035, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + BEAT * 0.9);
    osc.connect(lp).connect(g).connect(this.master);
    osc.start(t);
    osc.stop(t + BEAT);
  }
}

export default function MusicToggle() {
  const [on, setOn] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [volume, setVolume] = useState(() => {
    const v = parseFloat(localStorage.getItem("vg_music_vol"));
    return Number.isFinite(v) ? v : 0.5;
  });
  const synthRef = useRef(null);

  const startMusic = () => {
    if (!synthRef.current) synthRef.current = new SynthLoop();
    synthRef.current.setVolume(volume);
    synthRef.current.start();
    setOn(true);
    localStorage.setItem("vg_music_on", "1");
  };

  const stopMusic = () => {
    synthRef.current?.stop();
    setOn(false);
    localStorage.setItem("vg_music_on", "0");
  };

  // Restore preference after the first user gesture (browser autoplay policy)
  useEffect(() => {
    if (localStorage.getItem("vg_music_on") !== "1") return;
    const handler = () => {
      startMusic();
      window.removeEventListener("pointerdown", handler);
    };
    window.addEventListener("pointerdown", handler);
    return () => window.removeEventListener("pointerdown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    synthRef.current?.setVolume(volume);
    localStorage.setItem("vg_music_vol", String(volume));
  }, [volume]);

  useEffect(() => () => synthRef.current?.stop(), []);

  return (
    <div className="fixed bottom-4 right-4 z-[9990] flex flex-col items-end gap-2" data-testid="music-widget">
      {expanded && (
        <div className="flex items-center gap-3 border border-cyan-400/40 bg-[#0A1224]/95 px-4 py-3 shadow-[0_0_18px_rgba(0,240,255,0.25)]">
          <VolumeX className="w-4 h-4 text-cyan-300" />
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={volume}
            onChange={(e) => setVolume(parseFloat(e.target.value))}
            className="w-28 accent-cyan-400"
            data-testid="audio-volume-slider"
            aria-label="Music volume"
          />
          <Music className="w-4 h-4 text-cyan-300" />
        </div>
      )}
      <div className="flex items-center gap-1">
        <button
          onClick={() => setExpanded((e) => !e)}
          className="border border-cyan-400/40 bg-[#0A1224]/95 text-cyan-300 px-2 py-3 hover:bg-cyan-400/10"
          aria-label={expanded ? "Hide volume" : "Show volume"}
          data-testid="audio-expand-button"
        >
          {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
        </button>
        <button
          onClick={() => (on ? stopMusic() : startMusic())}
          className={`flex items-center gap-2 border px-4 py-2 font-mono text-[11px] uppercase tracking-widest transition-colors ${
            on
              ? "border-cyan-400 bg-cyan-400/15 text-cyan-300 shadow-[0_0_18px_rgba(0,240,255,0.35)]"
              : "border-cyan-400/40 bg-[#0A1224]/95 text-zinc-400 hover:text-cyan-300 hover:border-cyan-400"
          }`}
          data-testid="audio-toggle-button"
          aria-pressed={on}
        >
          {/* EQ bars */}
          <span className="flex items-end gap-[2px] h-3 w-4">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className={`w-1 bg-current ${on ? "eq-bar" : "h-[3px]"}`}
                style={on ? { animationDelay: `${i * 0.18}s` } : undefined}
              />
            ))}
          </span>
          <span data-testid="audio-status">{on ? "Synth Audio: On" : "Synth Audio: Off"}</span>
        </button>
      </div>
    </div>
  );
}
